import { useState, useEffect, useMemo, useCallback } from "react";
import posthog from "@/lib/posthog";
import { differenceInDays, parseISO, formatDistanceToNow } from "date-fns";
import {
  ArrowLeft,
  AlertTriangle,
  X,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Clock,
  XCircle,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  LayoutList,
  Users,
} from "lucide-react";
import { Invoice } from "@blinkbooks/types";
import apiClient from "@/utils/httpClient";
import { formatAmount, formatDate } from "@/utils/expenseFormatters";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// ── Types ─────────────────────────────────────────────────────────────────────

type SortField = 'customer' | 'invoice' | 'amount' | 'overdue' | 'status' | 'due_date';
type SortDir = 'asc' | 'desc';
type ViewMode = 'flat' | 'grouped';

interface AtRiskInvoice extends Invoice {
  daysOverdue: number;
}

interface CustomerSummary {
  name: string;
  totalOutstanding: number;
  invoiceCount: number;
  avgDaysOverdue: number;
  invoices: AtRiskInvoice[];
}

interface AtRiskCashProps {
  onBack: () => void;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const today = new Date();
today.setHours(0, 0, 0, 0);

function computeDaysOverdue(dueDate: string): number {
  try {
    const due = parseISO(dueDate);
    const diff = differenceInDays(today, due);
    return Math.max(0, diff);
  } catch {
    return 0;
  }
}

function isAtRisk(inv: Invoice): boolean {
  if (inv.balance <= 0) return false;
  if (inv.status === "paid" || inv.status === "void" || inv.status === "draft") return false;
  if (inv.status === "overdue") return true;
  // open / partially_paid past due date
  try {
    return parseISO(inv.dueDate) < today;
  } catch {
    return false;
  }
}

// ── Sub-components ────────────────────────────────────────────────────────────

const DaysOverdueBadge = ({ days }: { days: number }) => {
  if (days === 0) return <span className="text-sm text-muted-foreground">Due today</span>;
  const cls =
    days > 30
      ? "bg-red-100 text-red-700 border-red-200"
      : days > 15
      ? "bg-amber-100 text-amber-700 border-amber-200"
      : "bg-gray-100 text-gray-600 border-gray-200";
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${cls}`}>
      <Clock className="h-3 w-3" />
      {days}d overdue
    </span>
  );
};

// ── Main component ─────────────────────────────────────────────────────────────

const AtRiskCash = ({ onBack }: AtRiskCashProps) => {
  const [invoices, setInvoices] = useState<AtRiskInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);

  // Filters
  const [customerFilter, setCustomerFilter] = useState<string>("all");
  const [minDays, setMinDays] = useState<number>(0);
  const [minAmount, setMinAmount] = useState<number>(0);

  // Sort & view
  const [sortField, setSortField] = useState<SortField>("amount");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [viewMode, setViewMode] = useState<ViewMode>("flat");
  const [expandedCustomers, setExpandedCustomers] = useState<Set<string>>(new Set());

  // Side panel
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setFetchError(false);
      // Fetch overdue invoices (server-marked) + open/partially_paid (we filter by date client-side)
      const [overdueRes, openRes] = await Promise.all([
        apiClient.get<Invoice[]>("/invoices", { status: "overdue", limit: 100, page: 1 }),
        apiClient.get<Invoice[]>("/invoices", { status: "open", limit: 100, page: 1 }),
      ]);

      const rawInvoices: Invoice[] = [
        ...(Array.isArray(overdueRes) ? overdueRes : (overdueRes as { items?: Invoice[] }).items ?? []),
        ...(Array.isArray(openRes) ? openRes : (openRes as { items?: Invoice[] }).items ?? []),
      ];

      // Deduplicate by id
      const seen = new Set<string>();
      const atRisk: AtRiskInvoice[] = [];
      for (const inv of rawInvoices) {
        if (seen.has(inv.id)) continue;
        seen.add(inv.id);
        if (isAtRisk(inv)) {
          atRisk.push({ ...inv, daysOverdue: computeDaysOverdue(inv.dueDate) });
        }
      }

      atRisk.sort((a, b) => b.balance - a.balance);
      setInvoices(atRisk);
      setRefreshedAt(new Date());
    } catch (err) {
      console.error("AtRiskCash fetch error:", err);
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    posthog.capture("at_risk_cash_viewed");
  }, [fetchData]);

  // ── Filtered rows ──────────────────────────────────────────────────────────

  const filtered = useMemo(() => {
    return invoices.filter((inv) => {
      if (inv.daysOverdue < minDays) return false;
      if (inv.balance < minAmount) return false;
      if (customerFilter !== "all" && inv.customerName !== customerFilter) return false;
      return true;
    });
  }, [invoices, minDays, minAmount, customerFilter]);

  // ── Totals ─────────────────────────────────────────────────────────────────

  const totalAtRisk = useMemo(
    () => filtered.reduce((s, inv) => s + inv.balance, 0),
    [filtered]
  );

  // ── Customer grouping ──────────────────────────────────────────────────────

  const customerMap = useMemo<Map<string, CustomerSummary>>(() => {
    const map = new Map<string, CustomerSummary>();
    for (const inv of invoices) {
      const existing = map.get(inv.customerName);
      if (existing) {
        existing.totalOutstanding += inv.balance;
        existing.invoiceCount += 1;
        existing.avgDaysOverdue =
          (existing.avgDaysOverdue * (existing.invoiceCount - 1) + inv.daysOverdue) /
          existing.invoiceCount;
        existing.invoices.push(inv);
      } else {
        map.set(inv.customerName, {
          name: inv.customerName,
          totalOutstanding: inv.balance,
          invoiceCount: 1,
          avgDaysOverdue: inv.daysOverdue,
          invoices: [inv],
        });
      }
    }
    return map;
  }, [invoices]);

  // ── Customer dropdown list ─────────────────────────────────────────────────

  const customerList = useMemo(
    () => Array.from(customerMap.keys()).sort(),
    [customerMap]
  );

  // ── Sorted flat data ───────────────────────────────────────────────────────

  const sortedData = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let aV: string | number, bV: string | number;
      switch (sortField) {
        case "customer": aV = a.customerName; bV = b.customerName; break;
        case "invoice":  aV = a.invoiceNumber ?? a.id; bV = b.invoiceNumber ?? b.id; break;
        case "amount":   aV = a.balance; bV = b.balance; break;
        case "overdue":  aV = a.daysOverdue; bV = b.daysOverdue; break;
        case "status":   aV = a.status; bV = b.status; break;
        case "due_date": aV = new Date(a.dueDate).getTime(); bV = new Date(b.dueDate).getTime(); break;
        default: return 0;
      }
      if (aV < bV) return sortDir === "asc" ? -1 : 1;
      if (aV > bV) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [filtered, sortField, sortDir]);

  // ── Sorted grouped data ────────────────────────────────────────────────────

  const sortedGroups = useMemo(() => {
    const seen = new Set<string>();
    const groups: CustomerSummary[] = [];
    for (const inv of filtered) {
      if (!seen.has(inv.customerName)) {
        seen.add(inv.customerName);
        const s = customerMap.get(inv.customerName);
        if (s) groups.push(s);
      }
    }
    return groups.sort((a, b) => {
      switch (sortField) {
        case "customer": return sortDir === "asc" ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
        case "overdue":  return sortDir === "asc" ? a.avgDaysOverdue - b.avgDaysOverdue : b.avgDaysOverdue - a.avgDaysOverdue;
        case "amount":
        default:         return sortDir === "asc" ? a.totalOutstanding - b.totalOutstanding : b.totalOutstanding - a.totalOutstanding;
      }
    });
  }, [filtered, customerMap, sortField, sortDir]);

  // ── Derived insights ───────────────────────────────────────────────────────

  const insights = useMemo(() => {
    if (invoices.length === 0) return [];
    const total = invoices.reduce((s, i) => s + i.balance, 0);
    const sorted = [...customerMap.values()].sort(
      (a, b) => b.totalOutstanding - a.totalOutstanding
    );
    const result: string[] = [];

    // Concentration risk
    if (sorted.length >= 3) {
      const top3 = sorted.slice(0, 3).reduce((s, c) => s + c.totalOutstanding, 0);
      const pct = Math.round((top3 / total) * 100);
      result.push(`${pct}% of at-risk amount comes from ${Math.min(3, sorted.length)} customers`);
    }

    // Avg delay
    const avgDelay =
      invoices.reduce((s, i) => s + i.daysOverdue, 0) / invoices.length;
    result.push(`Average overdue delay is ${Math.round(avgDelay)} days`);

    // Largest single invoice
    const largest = [...invoices].sort((a, b) => b.balance - a.balance)[0];
    if (largest) {
      result.push(
        `Largest exposure: ${formatAmount(largest.balance)} — ${largest.customerName}${largest.daysOverdue > 0 ? `, ${largest.daysOverdue} days overdue` : ""}`
      );
    }

    return result;
  }, [invoices, customerMap]);

  // ── Selected invoice / customer ────────────────────────────────────────────

  const selectedInvoice = useMemo(
    () => (selectedInvoiceId ? invoices.find((i) => i.id === selectedInvoiceId) ?? null : null),
    [selectedInvoiceId, invoices]
  );
  const selectedCustomer = useMemo(
    () => (selectedInvoice ? customerMap.get(selectedInvoice.customerName) ?? null : null),
    [selectedInvoice, customerMap]
  );

  // ── Suggested actions ──────────────────────────────────────────────────────

  const suggestedActions = useMemo(() => {
    if (!selectedInvoice) return [];
    const actions: string[] = [];
    if (selectedInvoice.daysOverdue > 30) {
      actions.push("Escalate — delay is significantly above normal");
    }
    if (selectedInvoice.daysOverdue > 0) {
      actions.push("Follow up with customer (high priority)");
    }
    if ((selectedCustomer?.invoiceCount ?? 0) > 1) {
      actions.push(
        `Review all ${selectedCustomer!.invoiceCount} open invoices for this customer`
      );
    }
    if (selectedInvoice.balance > 10000) {
      actions.push("Consider payment plan or escalate to management");
    }
    if (actions.length === 0) actions.push("Monitor payment — invoice is approaching due date");
    return actions;
  }, [selectedInvoice, selectedCustomer]);

  // ── Sub-components ────────────────────────────────────────────────────────

  const SortableHeader = ({ field, children, className = "" }: { field: SortField; children: React.ReactNode; className?: string }) => {
    const isActive = sortField === field;
    return (
      <th
        className={`px-4 py-3 font-medium text-muted-foreground text-xs uppercase tracking-wide cursor-pointer hover:text-foreground select-none ${className}`}
        onClick={() => {
          if (sortField === field) {
            const newDir = sortDir === "asc" ? "desc" : "asc";
            setSortDir(newDir);
            posthog.capture("at_risk_sorted", { column: field, direction: newDir });
          } else {
            setSortField(field);
            setSortDir("asc");
            posthog.capture("at_risk_sorted", { column: field, direction: "asc" });
          }
        }}
      >
        <div className={`flex items-center gap-1 ${className.includes("text-right") ? "justify-end" : ""}`}>
          {children}
          {isActive
            ? sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
            : <ArrowUpDown className="h-3 w-3 opacity-30" />}
        </div>
      </th>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex flex-col h-full min-h-0 bg-background">
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-card shadow-sm flex-shrink-0">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
            <div className="h-5 w-px bg-border" />
            <div>
              <h1 className="text-xl font-bold text-foreground leading-tight">At Risk Cash</h1>
              {!loading && !fetchError && (
                <p className="text-sm text-muted-foreground">
                  {formatAmount(totalAtRisk)} &bull; {filtered.length} invoice{filtered.length !== 1 ? "s" : ""}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {refreshedAt && (
              <span className="text-xs text-muted-foreground hidden sm:inline">
                Updated {formatDistanceToNow(refreshedAt, { addSuffix: true })}
              </span>
            )}
            <button
              onClick={fetchData}
              disabled={loading}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center gap-3 py-24 text-muted-foreground">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <span className="text-sm">Loading at-risk invoices...</span>
          </div>
        ) : fetchError ? (
          <div className="flex flex-col items-center gap-3 py-24 text-muted-foreground">
            <XCircle className="h-8 w-8 text-red-400" />
            <p className="text-sm font-medium text-red-600">Failed to load invoices</p>
            <button onClick={fetchData} className="text-xs text-primary hover:underline">
              Try again
            </button>
          </div>
        ) : invoices.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-24 text-muted-foreground">
            <CheckCircle2 className="h-10 w-10 text-emerald-500" />
            <p className="text-base font-medium text-emerald-700">No at-risk cash</p>
            <p className="text-sm text-muted-foreground">All invoices are within terms.</p>
          </div>
        ) : (
          <div className="flex-1 overflow-hidden flex flex-col min-h-0">
            {/* ── Insight Summary Bar ───────────────────────────────────── */}
            <div className="px-6 pt-4 pb-2 flex-shrink-0">
              <div className="rounded-xl border bg-amber-50 border-amber-200 p-4 space-y-1.5">
                {insights.map((insight, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-amber-800">
                    <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                    <span>{insight}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Controls ─────────────────────────────────────────────── */}
            <div className="px-6 py-3 flex flex-wrap items-center gap-3 flex-shrink-0 border-b">
              {/* Customer dropdown (closed list) */}
              <select
                value={customerFilter}
                onChange={(e) => setCustomerFilter(e.target.value)}
                className="h-8 px-2 text-sm rounded-md border bg-background focus:outline-none focus:ring-1 focus:ring-primary max-w-[180px]"
              >
                <option value="all">All Customers</option>
                {customerList.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>

              {/* Overdue threshold */}
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <span>Overdue &gt;</span>
                <select
                  value={minDays}
                  onChange={(e) => setMinDays(Number(e.target.value))}
                  className="h-8 px-2 text-sm rounded-md border bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value={0}>Any</option>
                  <option value={15}>15 days</option>
                  <option value={30}>30 days</option>
                  <option value={60}>60 days</option>
                  <option value={90}>90 days</option>
                </select>
              </div>

              {/* Min amount */}
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <span>Amount &gt;</span>
                <select
                  value={minAmount}
                  onChange={(e) => setMinAmount(Number(e.target.value))}
                  className="h-8 px-2 text-sm rounded-md border bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value={0}>Any</option>
                  <option value={1000}>$1,000</option>
                  <option value={5000}>$5,000</option>
                  <option value={10000}>$10,000</option>
                  <option value={25000}>$25,000</option>
                </select>
              </div>

              {(minDays > 0 || minAmount > 0 || customerFilter !== "all") && (
                <button
                  onClick={() => { setMinDays(0); setMinAmount(0); setCustomerFilter("all"); }}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X className="h-3 w-3" />
                  Clear filters
                </button>
              )}

              {/* View toggle */}
              <div className="ml-auto flex items-center gap-1 rounded-lg border p-0.5 bg-muted/30">
                <button
                  onClick={() => { setViewMode("flat"); posthog.capture("at_risk_view_mode_changed", { mode: "flat" }); }}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${viewMode === "flat" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                >
                  <LayoutList className="h-3 w-3" />
                  By Invoice
                </button>
                <button
                  onClick={() => { setViewMode("grouped"); posthog.capture("at_risk_view_mode_changed", { mode: "grouped" }); }}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${viewMode === "grouped" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                >
                  <Users className="h-3 w-3" />
                  By Customer
                </button>
              </div>

              <span className="text-xs text-muted-foreground whitespace-nowrap">
                {viewMode === "grouped"
                  ? `${sortedGroups.length} customer${sortedGroups.length !== 1 ? "s" : ""}`
                  : `${filtered.length} of ${invoices.length} invoice${invoices.length !== 1 ? "s" : ""}`}
                {" "}&bull; {formatAmount(totalAtRisk)} total
              </span>
            </div>

            {/* ── Main table + side panel ───────────────────────────────── */}
            <div className="flex-1 overflow-hidden flex min-h-0">
              {/* Table */}
              <div className={`flex-1 overflow-auto min-w-0 ${selectedInvoice ? "border-r" : ""}`}>
                {filtered.length === 0 ? (
                  <div className="flex flex-col items-center gap-2 py-16 text-muted-foreground">
                    <AlertCircle className="h-8 w-8" />
                    <p className="text-sm">No invoices match the current filters.</p>
                  </div>
                ) : viewMode === "grouped" ? (
                  /* ── By Customer grouped view ── */
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40 sticky top-0 z-10">
                      <tr className="text-left">
                        <SortableHeader field="customer">Customer</SortableHeader>
                        <th className="px-4 py-3 font-medium text-muted-foreground text-xs uppercase tracking-wide"># Invoices</th>
                        <SortableHeader field="amount" className="text-right">Total Outstanding</SortableHeader>
                        <SortableHeader field="overdue">Avg Days Overdue</SortableHeader>
                        <th className="px-4 py-3 w-8" />
                      </tr>
                    </thead>
                    <tbody>
                      {sortedGroups.map((grp) => {
                        const isExpanded = expandedCustomers.has(grp.name);
                        return (
                          <>
                            <tr key={grp.name}
                              className="border-t cursor-pointer hover:bg-muted/30 transition-colors"
                              onClick={() => setExpandedCustomers(prev => {
                                const next = new Set(prev);
                                if (isExpanded) next.delete(grp.name); else next.add(grp.name);
                                return next;
                              })}
                            >
                              <td className="px-4 py-3 font-semibold text-foreground">{grp.name}</td>
                              <td className="px-4 py-3 text-muted-foreground">{grp.invoiceCount}</td>
                              <td className="px-4 py-3 text-right font-bold text-red-600 tabular-nums">{formatAmount(grp.totalOutstanding)}</td>
                              <td className="px-4 py-3">
                                <DaysOverdueBadge days={Math.round(grp.avgDaysOverdue)} />
                              </td>
                              <td className="px-4 py-3 text-muted-foreground">
                                {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                              </td>
                            </tr>
                            {isExpanded && grp.invoices.map((inv) => {
                              const isSelected = selectedInvoiceId === inv.id;
                              return (
                                <tr key={inv.id}
                                  onClick={() => setSelectedInvoiceId(isSelected ? null : inv.id)}
                                  className={`border-t cursor-pointer transition-colors ${isSelected ? "bg-primary/5 border-l-2 border-l-primary" : "bg-muted/10 hover:bg-muted/20"}`}
                                >
                                  <td className="px-4 py-2 pl-8 text-muted-foreground text-xs">
                                    {inv.invoiceNumber ? `#${inv.invoiceNumber}` : inv.id.slice(0, 8)}
                                  </td>
                                  <td className="px-4 py-2 text-xs text-muted-foreground">—</td>
                                  <td className="px-4 py-2 text-right font-medium text-foreground tabular-nums text-xs">{formatAmount(inv.balance)}</td>
                                  <td className="px-4 py-2"><DaysOverdueBadge days={inv.daysOverdue} /></td>
                                  <td />
                                </tr>
                              );
                            })}
                          </>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  /* ── By Invoice flat view ── */
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40 sticky top-0 z-10">
                      <tr className="text-left">
                        <SortableHeader field="customer">Customer</SortableHeader>
                        <SortableHeader field="invoice">Invoice</SortableHeader>
                        <SortableHeader field="amount" className="text-right">Amount</SortableHeader>
                        <SortableHeader field="overdue">Overdue</SortableHeader>
                        <SortableHeader field="status">Status</SortableHeader>
                        <SortableHeader field="due_date">Due Date</SortableHeader>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedData.map((inv) => {
                        const isSelected = selectedInvoiceId === inv.id;
                        return (
                          <tr
                            key={inv.id}
                            onClick={() => setSelectedInvoiceId(isSelected ? null : inv.id)}
                            className={`border-t cursor-pointer transition-colors group ${
                              isSelected
                                ? "bg-primary/5 border-l-2 border-l-primary"
                                : "hover:bg-muted/30"
                            }`}
                          >
                            <td className="px-4 py-3 font-medium text-foreground">
                              {inv.customerName}
                            </td>
                            <td className="px-4 py-3 text-muted-foreground">
                              {inv.invoiceNumber ? `#${inv.invoiceNumber}` : inv.id.slice(0, 8)}
                            </td>
                            <td className="px-4 py-3 text-right font-bold text-foreground tabular-nums">
                              {formatAmount(inv.balance)}
                            </td>
                            <td className="px-4 py-3">
                              <DaysOverdueBadge days={inv.daysOverdue} />
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full border ${
                                inv.status === "overdue"
                                  ? "bg-red-50 text-red-700 border-red-200"
                                  : inv.status === "partially_paid"
                                  ? "bg-blue-50 text-blue-700 border-blue-200"
                                  : "bg-orange-50 text-orange-700 border-orange-200"
                              }`}>
                                {inv.status.replace(/_/g, " ")}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-muted-foreground">
                              {formatDate(inv.dueDate)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Side Panel */}
              {selectedInvoice && selectedCustomer && (
                <div className="w-80 xl:w-96 flex-shrink-0 overflow-y-auto bg-card">
                  <div className="p-5 space-y-5">
                    {/* Close */}
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-foreground">{selectedCustomer.name}</h3>
                      <button
                        onClick={() => setSelectedInvoiceId(null)}
                        className="text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>

                    {/* Customer Snapshot */}
                    <div className="rounded-xl border bg-muted/30 p-4 space-y-2">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Customer Snapshot
                      </p>
                      <div className="grid grid-cols-2 gap-3 mt-2">
                        <div>
                          <p className="text-xs text-muted-foreground">Total Outstanding</p>
                          <p className="text-base font-bold text-red-600 tabular-nums">
                            {formatAmount(selectedCustomer.totalOutstanding)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Open Invoices</p>
                          <p className="text-base font-bold text-foreground">
                            {selectedCustomer.invoiceCount}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Avg Overdue</p>
                          <p className={`text-base font-bold ${selectedCustomer.avgDaysOverdue > 30 ? "text-red-600" : "text-amber-600"}`}>
                            {Math.round(selectedCustomer.avgDaysOverdue)}d
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Invoice Details */}
                    <div className="rounded-xl border p-4 space-y-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Selected Invoice
                      </p>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Invoice</span>
                          <span className="font-medium">
                            {selectedInvoice.invoiceNumber
                              ? `#${selectedInvoice.invoiceNumber}`
                              : selectedInvoice.id.slice(0, 8)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Balance</span>
                          <span className="font-bold text-red-600">{formatAmount(selectedInvoice.balance)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Total Amount</span>
                          <span className="font-medium">{formatAmount(selectedInvoice.totalAmount)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Due Date</span>
                          <span className="font-medium">{formatDate(selectedInvoice.dueDate)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Days Overdue</span>
                          <span className={`font-bold ${selectedInvoice.daysOverdue > 30 ? "text-red-600" : selectedInvoice.daysOverdue > 0 ? "text-amber-600" : "text-muted-foreground"}`}>
                            {selectedInvoice.daysOverdue > 0 ? `${selectedInvoice.daysOverdue} days` : "Due today"}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* All invoices for this customer */}
                    {selectedCustomer.invoices.length > 1 && (
                      <div className="rounded-xl border p-4 space-y-2">
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          All Open ({selectedCustomer.invoices.length})
                        </p>
                        <div className="space-y-1.5 max-h-40 overflow-y-auto">
                          {selectedCustomer.invoices.map((inv) => (
                            <button
                              key={inv.id}
                              onClick={() => setSelectedInvoiceId(inv.id)}
                              className={`w-full flex items-center justify-between text-xs px-2 py-1.5 rounded-md transition-colors ${
                                inv.id === selectedInvoiceId
                                  ? "bg-primary/10 text-primary"
                                  : "hover:bg-muted/50 text-muted-foreground"
                              }`}
                            >
                              <span>
                                {inv.invoiceNumber ? `#${inv.invoiceNumber}` : inv.id.slice(0, 8)}
                              </span>
                              <span className="font-medium">{formatAmount(inv.balance)}</span>
                              <DaysOverdueBadge days={inv.daysOverdue} />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Suggested Actions */}
                    <div className="rounded-xl border bg-sky-50 border-sky-200 p-4 space-y-2">
                      <p className="text-xs font-semibold uppercase tracking-wide text-sky-700">
                        Recommended Actions
                      </p>
                      <ul className="space-y-1.5">
                        {suggestedActions.map((action, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-sky-800">
                            <span className="mt-1 h-1.5 w-1.5 rounded-full bg-sky-500 shrink-0" />
                            {action}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </TooltipProvider>
  );
};

export default AtRiskCash;
